import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ZoomDetectionService } from '../../services/zoom-detection.service';

export interface CustomAutocompleteOption {
  value: any;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-custom-autocomplete',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './custom-autocomplete.component.html',
  styleUrls: ['./custom-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomAutocompleteComponent),
      multi: true,
    },
  ],
})
export class CustomAutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() options: CustomAutocompleteOption[] = [];
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() appearance: 'outline' | 'fill' = 'outline';
  @Input() errorMessage: string = '';
  @Input() showError: boolean = false;
  @Input() debounceTime: number = 300;
  @Input() minSearchLength: number = 0;
  @Input() showClearButton: boolean = true;
  @Input() showFullOptions: boolean = false;

  @Output() selectionChange = new EventEmitter<any>();
  @Output() inputChange = new EventEmitter<string>();
  @Output() optionSelected = new EventEmitter<CustomAutocompleteOption>();

  @ViewChild('autocompleteContainer', { static: true }) autocompleteContainer!: ElementRef;
  @ViewChild('inputElement', { static: true }) inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown', { static: false }) dropdown!: ElementRef;

  isOpen = false;
  inputValue = '';
  selectedValue: any = null;
  selectedOption: CustomAutocompleteOption | null = null;
  focused = false;
  filteredOptions: CustomAutocompleteOption[] = [];
  highlightedIndex = -1;

  private destroy$ = new Subject<void>();
  private inputChange$ = new Subject<string>();
  private dropdownInteractionActive = false;
  private lastDropdownInteractionAt = 0;
  private touchFocusArmed = false;
  private keyboardRepositionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(private zoomDetectionService: ZoomDetectionService) {}

  ngOnInit() {
    // Setup input change debouncing
    this.inputChange$
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => {
        this.filterOptions(value);
        this.inputChange.emit(value);
      });

    // Initialize filtered options
    this.updateFilteredOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update filtered options when options input changes
    if (changes['options'] && !changes['options'].firstChange) {
      if (this.showFullOptions) {
        this.filteredOptions = [...this.options];
        this.openDropdown();
      } else {
        this.updateFilteredOptions();
      }
    }
  }

  ngOnDestroy() {
    this.clearKeyboardRepositionTimeout();
    this.detachDropdownPositioning();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.autocompleteContainer.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  onInputFocus() {
    this.focused = true;
    this.touchFocusArmed = false;
    this.openDropdown();
    this.scheduleKeyboardReposition();
  }

  onInputBlur() {
    this.focused = false;
    this.touchFocusArmed = false;
    this.clearKeyboardRepositionTimeout();
    this.onTouched();
    // Delay closing to allow option selection
    setTimeout(() => {
      if (this.shouldKeepDropdownOpenOnBlur()) {
        return;
      }

      if (!this.autocompleteContainer.nativeElement.contains(document.activeElement)) {
        this.closeDropdown();
      }
    }, 150);
  }

  onInputPointerDown(event: PointerEvent) {
    if (this.disabled || !this.shouldDelayTouchFocus(event)) {
      return;
    }

    event.preventDefault();
    this.touchFocusArmed = true;
    this.openDropdown();
  }

  onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.inputValue = target.value;
    this.highlightedIndex = -1;

    // Always update form control value when typing
    this.inputChange.emit(this.inputValue);

    // Clear selection if input doesn't match selected option
    if (this.selectedOption && this.inputValue !== this.selectedOption.label) {
      this.selectedValue = null;
      this.selectedOption = null;
      this.selectionChange.emit(null);
    }

    this.inputChange$.next(this.inputValue);

    if (this.inputValue.length >= this.minSearchLength) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }

  onInputKeyDown(event: KeyboardEvent) {
    if (this.disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen && this.inputValue.length >= this.minSearchLength) {
          this.openDropdown();
        } else {
          this.navigateOptions(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateOptions(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.isOpen && this.highlightedIndex >= 0) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.closeDropdown();
        break;
    }
  }

  onArrowPointerDown(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onArrowClick(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) {
      this.closeDropdown();
      return;
    }

    this.inputElement.nativeElement.focus();
    this.openDropdown();
  }

  onDropdownPointerDown(): void {
    this.dropdownInteractionActive = true;
    this.lastDropdownInteractionAt = Date.now();
  }

  onDropdownPointerMove(): void {
    if (!this.dropdownInteractionActive) {
      return;
    }

    this.lastDropdownInteractionAt = Date.now();
  }

  onDropdownPointerUp(): void {
    this.dropdownInteractionActive = false;
    this.lastDropdownInteractionAt = Date.now();
  }

  openDropdown() {
    if (this.disabled) return;
    // Update filtered options to ensure they're current
    this.updateFilteredOptions();
    // Show all options if showFullOptions is enabled
    if (this.showFullOptions || this.inputValue.length === 0) {
      this.filteredOptions = [...this.options];
    }
    if (this.filteredOptions.length > 0) {
      this.isOpen = true;

      setTimeout(() => {
        this.attachDropdownPositioning();
      });
    }
  }

  closeDropdown() {
    this.clearKeyboardRepositionTimeout();
    this.detachDropdownPositioning();
    this.isOpen = false;
    this.highlightedIndex = -1;
    this.dropdownInteractionActive = false;
    this.touchFocusArmed = false;
  }

  selectOption(option: CustomAutocompleteOption) {
    if (option.disabled) return;

    this.selectedValue = option.value;
    this.selectedOption = option;
    this.inputValue = option.label;
    this.onChange(option.value);
    this.selectionChange.emit(option.value);
    this.optionSelected.emit(option);
    this.closeDropdown();

    // Update input element value
    if (this.inputElement) {
      this.inputElement.nativeElement.value = option.label;
    }
  }

  private navigateOptions(direction: number) {
    if (this.filteredOptions.length === 0) return;

    const enabledOptions = this.filteredOptions.filter(opt => !opt.disabled);
    if (enabledOptions.length === 0) return;

    this.highlightedIndex += direction;

    if (this.highlightedIndex < 0) {
      this.highlightedIndex = enabledOptions.length - 1;
    } else if (this.highlightedIndex >= enabledOptions.length) {
      this.highlightedIndex = 0;
    }

    // Find the actual index in filteredOptions
    const targetOption = enabledOptions[this.highlightedIndex];
    this.highlightedIndex = this.filteredOptions.findIndex(opt => opt === targetOption);
  }

  private updateFilteredOptions() {
    // Initialize or update filtered options based on current input
    if (!this.inputValue || this.inputValue.length < this.minSearchLength) {
      this.filteredOptions = [...this.options];
    } else {
      this.filterOptions(this.inputValue);
    }
  }

  private filterOptions(searchTerm: string) {
    const term = this.toInputText(searchTerm).toLowerCase();
    if (!term || term.length < this.minSearchLength) {
      this.filteredOptions = [...this.options];
      return;
    }

    this.filteredOptions = this.options.filter(option => option.label.toLowerCase().includes(term));
  }

  /** The form control may hold a non-string value (e.g. a selected option object); the input text must be a string. */
  private toInputText(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  }

  get hasValue(): boolean {
    return this.inputValue !== null && this.inputValue !== undefined && this.inputValue !== '';
  }

  get isFloatingLabel(): boolean {
    const hasPlaceholder = this.placeholder.trim().length > 0;
    return this.hasValue || this.focused || this.isOpen || hasPlaceholder;
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.selectedValue = value;

    // Find the option that matches the value
    const matchingOption = this.options.find(opt => opt.value === value);
    if (matchingOption) {
      this.selectedOption = matchingOption;
      this.inputValue = matchingOption.label;
      if (this.inputElement) {
        this.inputElement.nativeElement.value = matchingOption.label;
      }
    } else {
      this.selectedOption = null;
      this.inputValue = this.toInputText(value);
      if (this.inputElement) {
        this.inputElement.nativeElement.value = this.inputValue;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  trackByValue(index: number, option: CustomAutocompleteOption): any {
    return option.value;
  }

  private attachDropdownPositioning(): void {
    if (!this.dropdown || !this.autocompleteContainer) {
      return;
    }

    this.zoomDetectionService.attachRepositionListeners({
      triggerElement: this.autocompleteContainer.nativeElement,
      overlayElement: this.dropdown.nativeElement,
      preferredMaxHeight: 256,
      matchTriggerWidth: true,
      horizontalAlign: 'start',
      preferredPlacement: 'bottom',
    });
  }

  private detachDropdownPositioning(): void {
    if (this.dropdown?.nativeElement) {
      this.zoomDetectionService.detachRepositionListeners(this.dropdown.nativeElement);
    }
  }

  private shouldDelayTouchFocus(event: PointerEvent): boolean {
    if (this.focused || this.touchFocusArmed) {
      return false;
    }

    if (event.pointerType === 'mouse') {
      return false;
    }

    if (event.pointerType === 'touch') {
      return true;
    }

    return window.matchMedia('(pointer: coarse)').matches;
  }

  private scheduleKeyboardReposition(): void {
    if (!window.matchMedia('(pointer: coarse)').matches) {
      return;
    }

    this.clearKeyboardRepositionTimeout();
    this.keyboardRepositionTimeoutId = setTimeout(() => {
      this.keyboardRepositionTimeoutId = null;
      if (this.isOpen) {
        this.attachDropdownPositioning();
      }
    }, 250);
  }

  private clearKeyboardRepositionTimeout(): void {
    if (this.keyboardRepositionTimeoutId !== null) {
      clearTimeout(this.keyboardRepositionTimeoutId);
      this.keyboardRepositionTimeoutId = null;
    }
  }

  private shouldKeepDropdownOpenOnBlur(): boolean {
    return this.dropdownInteractionActive || Date.now() - this.lastDropdownInteractionAt < 250;
  }
}
